import { AccountPanelRow, AccountRowKind } from './account-select-panel.model';
import { afterNextRender, AfterViewChecked, AfterViewInit, ChangeDetectionStrategy, Component, computed, ElementRef, inject, input, output, signal, viewChildren } from '@angular/core';
import { FlagsHelper, Permissions } from '@app/shared/models/icentral-constants.model';
import { IcScrollbarDirective } from '@app/shared/directives/ic-scrollbar.directive';
import { IcAccount } from '@app/api/account/models/ic-account.model';
import { UpperCasePipe } from '@angular/common';
import { IconSvgComponent } from '@app/shared/icon-svg/icon-svg.component';
import { MenuIconsComponent } from '@app/shared/icon-svg/menu-icons/menu-icons.component';
import { RouterLink } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

/** U+2022 BULLET with one ASCII space each side, verified against the design at byte level. */
const META_SEPARATOR = ' • ';

/**
 * Makes every id this component mints unique per INSTANCE, not merely per account.
 *
 * `aria-labelledby` and `aria-describedby` are resolved against the whole document, so two
 * panels alive at once -- the open overlay and a second instance in a test bed, which is
 * exactly what the spec file does -- would otherwise mint the same id twice and silently
 * point half the rows at the other panel's text. Nothing visible breaks; the announcement is
 * just wrong.
 */
let instanceCounter = 0;

/**
 * The Select Account panel body (Figma `Nav Modal`, 520 x 580, node 11852-13660).
 *
 * Deliberately presentational: accounts and the current selection come in as inputs, user
 * intent goes out as events carrying an account id or nothing. There is no HttpClient, no
 * Router, no manager service and no BroadcastService in here, which is what the abandoned
 * `feature/250902_toolbar_account_selector` attempt got wrong -- that version fetched
 * accounts, wrote user preferences and navigated, all from a setter, and so could not be
 * tested or reused without standing up five services.
 *
 * Expansion state is passed in rather than owned, so the container decides whether it
 * persists and this component stays a pure function of its inputs.
 *
 * Only one level of nesting is designed: sub-account rows are terminal and carry no chevron.
 *
 * The keyboard model, in one place, because the p-dropdown this replaced gave all of it away
 * for free and none of it is free now:
 *
 * - The panel takes focus when it is created, landing on the selected row or the first.
 * - Up/Down walk the rows in VISUAL order, which is what `rows` flattens the two levels into,
 *   so the row below a last child is the next parent. Home/End reach either end. Neither
 *   arrow wraps.
 * - Right/Left expand and collapse, rather than Enter on a focused chevron, so that Tab stays
 *   free for the row's two buttons. Right also steps into an open parent's first child and
 *   Left climbs a child back to its parent.
 * - Enter and Space select, and do nothing at all on a locked row, which is what the
 *   `aria-disabled` on that row promises.
 * - The rows carry a ROVING tabindex: exactly one row, and only that row's buttons and PAY NOW
 *   anchor, are in the document tab order at a time. Tab from a focused row therefore enters
 *   that row's controls and then leaves the list, instead of walking N rows x 2 buttons.
 * - Focus is held by account id, not by index, so it survives both expansion changes and the
 *   whole-list rebuild AB#21934 can fire underneath an open panel.
 *
 * Dismissal, and returning focus to the trigger afterwards, belong to the container -- the
 * panel does not know it is in an overlay.
 *
 * ---
 *
 * THE ARIA PATTERN, AND WHY IT IS NOT A LISTBOX
 *
 * The list is `role="tree"` with `role="treeitem"` rows. That choice was forced, not preferred:
 *
 * - A listbox was the obvious reading of "pick one account from a list", and it is wrong here.
 *   `role="option"` may not contain interactive descendants, and EVERY row in this panel has
 *   two buttons and sometimes a PAY NOW link. A listbox with focusable children is the kind of
 *   mistake that lints clean -- the option still has a name, the listbox still has options --
 *   and then strands a screen-reader user who can see a button they cannot reach, because
 *   browse mode does not expose controls inside an option.
 * - `tree` carries `aria-expanded` as part of the role rather than as something bolted on, and
 *   the Up/Down/Left/Right/Home/End model this component already implements IS the WAI-ARIA
 *   tree keyboard pattern. The semantics were being described accurately by the behaviour
 *   before they were described accurately by the roles.
 * - `treeitem` permits `aria-selected`, so "which account is in effect" survives the move.
 *
 * The tree is rendered FLAT: one `@for` over {@link rows}, with a parent's children as
 * siblings rather than inside a `role="group"`. That is a deliberate trade. A nested DOM would
 * give levels and set counts for free, but it would also break the single flat `rowElements`
 * array the whole keyboard model indexes into, and turn every arrow key back into a tree walk.
 * The cost of staying flat is that `aria-level`, `aria-posinset` and `aria-setsize` must be
 * supplied by hand on every row -- which the rows do. A flat tree WITHOUT them is the genuinely
 * broken version: it announces screen positions as if they were positions in a level.
 *
 * What is deliberately NOT here:
 *
 * - No `role="dialog"` on the panel. A dialog promises a focus trap, and this panel promises
 *   the opposite -- Tab is specified to leave the list after the active row's own controls. A
 *   role that contradicts the behaviour is worse than no role.
 * - No `aria-activedescendant`. Focus genuinely moves between rows here (roving tabindex), and
 *   the two patterns are alternatives, not complements.
 * - The panel's accessible name comes from `aria-labelledby` on the tree pointing at the
 *   visible `<h2>`, rather than from an `aria-label` holding a second copy of the same string.
 */
@Component({
    selector: 'ic-account-select-panel',
    templateUrl: './account-select-panel.component.html',
    styleUrls: ['./account-select-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    // RouterLink is for the PAY NOW anchor only. The component imports no Router SERVICE -
    // the no-restricted-imports rule scoped to this folder still forbids that.
    imports: [IconSvgComponent, MenuIconsComponent, RouterLink, TranslatePipe, UpperCasePipe, IcScrollbarDirective]
})
export class AccountSelectPanelComponent implements AfterViewChecked, AfterViewInit {
    private translate = inject(TranslateService);

    /**
     * The row elements, in the same order as {@link rows}, so an index into one is an index
     * into the other. Focus is moved by calling `focus()` on these rather than by binding an
     * `autofocus`-style attribute, because the row that should hold focus changes with every
     * keystroke and only one of them is ever in the tab order.
     */
    private readonly rowElements = viewChildren<ElementRef<HTMLElement>>('rowElement');

    /**
     * False until the panel has rendered once. Sub rows grow in and shrink out as their account is expanded or collapsed
     * (ER-1572), but the rows already expanded when the panel opens should simply be there, not grow in with it.
     */
    protected readonly settled = signal(false);

    constructor() {
        afterNextRender(() => this.settled.set(true));
    }

    // =========================================================================================================================================================
    // Inputs
    // =========================================================================================================================================================

    /** Top-level accounts, each carrying its own `subAccounts`. Render order is list order. */
    readonly accounts = input<readonly IcAccount[]>([]);

    /** Id of the account currently in effect. Its row shows the blue check in place of a glyph. */
    readonly selectedAccountId = input<string | null>(null);

    /** Ids whose children are showing. Owned by the container, not by this component. */
    readonly expandedAccountIds = input<readonly string[]>([]);

    /**
     * A 16px inset in place of the frame's 24px, for a placement too narrow for the full one: the
     * header's account segment, where 24px leaves a past-due row's PAY NOW short. Set by
     * toolbar-account-select from its placement.
     */
    readonly compact = input(false);

    // =========================================================================================================================================================
    // Outputs
    // =========================================================================================================================================================

    /** A row was chosen. Carries the account id; switching is the container's job. */
    readonly accountSelected = output<string>();

    /**
     * The chevron on a main-account row was pressed. Carries the account id. Never emitted for
     * a row with no sub-accounts, whose chevron is drawn but disabled.
     */
    readonly expandToggled = output<string>();

    /** The person-gear button on a row was pressed. Carries the account id. */
    readonly accountSettingsClicked = output<string>();

    /**
     * The PAY NOW link on a payment-locked row was pressed. Carries the account id.
     *
     * The anchor navigates on its own via routerLink - this exists so the container can shut
     * the overlay, which would otherwise still be hanging over the Billing page the user just
     * asked for. Navigation is emphatically NOT the container's to perform here; the link has
     * a real href precisely so it behaves like one.
     */
    readonly payNowClicked = output<string>();

    /** The header's `ACCOUNT SELECT` button was pressed. */
    readonly accountSelectClicked = output<void>();

    /** The empty state's create button was pressed. */
    readonly createAccountClicked = output<void>();

    // =========================================================================================================================================================
    // Keyboard State
    // =========================================================================================================================================================

    /**
     * The account whose row holds the list's single tab stop, and the row focus returns to.
     *
     * Held as an account id rather than an index on purpose. `rebuildAccountsList` (AB#21934)
     * replaces every account instance in the list while the panel may be open, and an index
     * would silently come to mean a different row -- or no row at all -- the moment that list
     * changed length or order.
     */
    private readonly focusedAccountId = signal<string | null>(null);

    /**
     * The element that last took focus, kept so that its DESTRUCTION can be told apart from the
     * user simply moving on. Those two look identical from `document.activeElement` alone, and
     * confusing them is how a focus-restoring list ends up yanking the user back out of
     * whatever they tabbed away to.
     */
    private focusedRowElement: HTMLElement | null = null;

    // =========================================================================================================================================================
    // Accessibility State
    // =========================================================================================================================================================

    /** See {@link instanceCounter}. Fixed at construction so ids are stable for this panel's life. */
    private readonly idPrefix = `ic-acct-panel-${++instanceCounter}`;

    // =========================================================================================================================================================
    // Properties
    // =========================================================================================================================================================

    /**
     * The bare numeral beside the title. Counts top-level accounts only, matching the design,
     * where a count of 5 sits above 7 visible rows because two of them are expanded children.
     */
    protected readonly accountCount = computed(() => this.accounts()?.length ?? 0);

    protected readonly isEmpty = computed(() => this.accountCount() === 0);

    /** Exposed so the template can glue segments together without redefining the separator. */
    protected get metaSeparator(): string {
        return META_SEPARATOR;
    }

    // =========================================================================================================================================================
    // Accessibility — element ids
    // =========================================================================================================================================================

    /**
     * The panel title's id, so the tree can be named BY the heading the user can see rather
     * than by a second copy of the same translated string in an `aria-label`.
     *
     * Two reasons for the indirection. It cannot drift: there is one string, rendered once,
     * and the name is whatever that heading says. And it satisfies WCAG 2.5.3 Label in Name
     * structurally rather than by inspection - a visible label and an accessible name that
     * were authored separately are two strings that will eventually disagree.
     */
    protected get titleId(): string {
        return `${this.idPrefix}-title`;
    }

    /**
     * The id of the span holding a row's account name. Every row's `aria-labelledby` points
     * here, which is what stops the row's accessible name being computed from its contents.
     *
     * Left to compute from contents, a `treeitem` takes the text of everything inside it -
     * including its own two buttons. The row would then announce as "Riverside Town Hall Park
     * #156651213 4/5 2 Sub accounts Account settings for Riverside Town Hall Park Show sub
     * accounts for Riverside Town Hall Park", which is a technically correct accessible name
     * and completely unusable in a list you are trying to move down.
     */
    protected rowNameId(row: AccountPanelRow): string {
        return `${this.idPrefix}-name-${row.account.accountID}`;
    }

    /** The id of a row's meta line, which is the row's DESCRIPTION rather than its name. */
    protected rowMetaId(row: AccountPanelRow): string {
        return `${this.idPrefix}-meta-${row.account.accountID}`;
    }

    /** The id of the screen-reader-only sentence explaining why a locked row cannot be chosen. */
    protected rowLockedReasonId(row: AccountPanelRow): string {
        return `${this.idPrefix}-locked-${row.account.accountID}`;
    }

    /**
     * What a row is DESCRIBED by, in announcement order: its meta line, then - on a locked row
     * only - the sentence saying why it cannot be selected.
     *
     * The BILL PAST DUE badge needs no id of its own: it is rendered inside the meta line, so
     * referencing the meta line already pulls the badge text into the description. That is the
     * association the feature asks for, and it comes for free from where the design put the
     * badge.
     *
     * The reason sentence comes second deliberately. A user who already knows this account is
     * past due hears the badge and moves on; a user who does not hears the badge and then the
     * explanation, in that order, instead of being made to sit through the explanation on
     * every pass.
     */
    protected rowDescribedBy(row: AccountPanelRow): string {
        const ids = [this.rowMetaId(row)];

        if (row.isLocked) {
            ids.push(this.rowLockedReasonId(row));
        }

        return ids.join(' ');
    }

    /**
     * `aria-disabled="false"` on a locked row's own controls, and nothing at all on any other
     * row's. This exists because `aria-disabled` is INHERITED by descendants.
     *
     * Found by reading Chrome's computed accessibility tree, not by reasoning about it, and
     * missed entirely by axe: putting `aria-disabled="true"` on a locked row silently disabled
     * everything inside it, so the PAY NOW link on a past-due account announced as unavailable.
     * That is the single worst thing this panel could say. The row is refusing SELECTION; the
     * link out to settle the bill is the one action such a user most needs, and the row's own
     * refusal was cancelling it.
     *
     * The chevron and the person-gear are overridden for the same reason: an unpaid bill stops
     * you switching INTO an account, not from looking at its settings or its sub-accounts. A
     * chevron with no sub-accounts behind it is the one exception - see {@link chevronAriaDisabled}.
     */
    protected controlDisabledOverride(row: AccountPanelRow): string | null {
        return row.isLocked ? 'false' : null;
    }

    /**
     * The chevron's `aria-disabled`: `'true'` on a main row with no sub-accounts, locked or not,
     * and otherwise the same {@link controlDisabledOverride} every other control on the row gets.
     *
     * A childless row's chevron is disabled in its own right rather than by inheritance, because
     * there is nothing for it to expand - so a locked row's `'false'` override must not reach it
     * and announce a live control that does nothing. A locked row WITH sub-accounts keeps the
     * override: the lockout refuses selection, not a look at the children.
     *
     * The attribute is the announcement only. A native button still activates under
     * `aria-disabled`, so the inertness is enforced in {@link onChevronClick}.
     */
    protected chevronAriaDisabled(row: AccountPanelRow): string | null {
        return row.hasChildren ? this.controlDisabledOverride(row) : 'true';
    }

    /**
     * Every row to render, in visual order: each main account followed by its children when
     * that account is expanded. Flattened here so the template stays a single `@for` and the
     * keyboard walk order in stage 4 is simply this array's order.
     */
    protected readonly rows = computed<AccountPanelRow[]>(() => {
        const rows: AccountPanelRow[] = [];
        const mainCount = this.accountCount();

        for (const [mainIndex, account] of (this.accounts() ?? []).entries()) {
            const expanded = this.isExpanded(account);
            rows.push(this.buildRow(account, 'main', expanded, mainIndex + 1, mainCount));

            if (!expanded) { continue; }

            // Counted against the parent's OWN children, not against the flattened list, so a
            // child announces "2 of 3" within its account rather than its position on screen.
            const subAccounts = account.subAccounts ?? [];
            for (const [subIndex, subAccount] of subAccounts.entries()) {
                rows.push(this.buildRow(subAccount, 'sub', false, subIndex + 1, subAccounts.length));
            }
        }

        const activeRowId = this.resolveActiveRowId(rows);
        for (const row of rows) {
            row.isActive = row.account.accountID === activeRowId;
        }

        return rows;
    });

    // =========================================================================================================================================================
    // Lifecycle
    // =========================================================================================================================================================

    /**
     * Move focus into the list as soon as the panel exists.
     *
     * The panel is created by the container's `@if` at the moment it opens and destroyed when
     * it closes, so "created" and "opened" are the same event and this hook runs exactly once
     * per opening. Doing it here rather than having the container reach in and call a method
     * keeps the container out of the panel's DOM, and means the panel behaves the same however
     * it was opened -- an overlay list that the keyboard has to be told to enter is an overlay
     * list most keyboard users never reach.
     */
    ngAfterViewInit(): void {
        this.focusActiveRow();
    }

    /**
     * Put focus back after a rebuild that destroyed the row holding it.
     *
     * `rebuildAccountsList` (AB#21934) fires whenever any account is edited anywhere in the
     * app, and it replaces every account instance in the list -- which can happen while this
     * panel is open and a user is halfway through a keyboard interaction with it. The rows are
     * tracked by account id, so in the ordinary case Angular moves the existing elements and
     * focus never moves; this exists for the case where an element genuinely goes, which is
     * what a naive implementation does on every rebuild and which drops focus on the body.
     *
     * See {@link restoreFocusAfterRebuild} for why this is not simply "re-focus if focus is not
     * where we left it".
     */
    ngAfterViewChecked(): void {
        this.restoreFocusAfterRebuild();
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    /**
     * A row took focus, by any route: the opening focus below, an arrow key, Tab back into the
     * list, or a mouse. Recording it here rather than at each of those call sites is what keeps
     * the tab stop and what the user can actually see in agreement.
     */
    protected onRowFocus(row: AccountPanelRow, event: FocusEvent): void {
        this.focusedAccountId.set(row.account.accountID);
        this.focusedRowElement = event.target as HTMLElement;
    }

    /**
     * A locked row does not activate, by mouse or by key.
     *
     * This is NOT a copy of the container's lockout rule, which is still the authority on what
     * happens to an id it is handed and still refuses one. It is this component honouring the
     * `aria-disabled="true"` it renders on the same row: an element that announces itself as
     * disabled and then activates anyway has lied to the one user who was listening.
     *
     * Mouse and keyboard are held together deliberately. If only the keyboard path were inert,
     * the row would behave differently depending on which hand reached it, which is exactly the
     * quiet divergence this file has been trying to avoid since it was written.
     */
    protected onRowClick(row: AccountPanelRow): void {
        if (row.isLocked) { return; }

        this.accountSelected.emit(row.account.accountID);
    }

    /**
     * Every key the row body answers to. `index` is the row's position in {@link rows}, which
     * is also its position in `rowElements`, so moving focus is an index arithmetic problem
     * rather than a tree walk -- the flattening in `rows` has already turned the two-level
     * hierarchy into the single visual sequence the user sees and the arrows have to follow.
     *
     * Keys pressed on the row's own buttons bubble up to here and are handed straight back:
     * Enter on the chevron must toggle expansion, not select the row underneath it. Comparing
     * `target` to `currentTarget` is what tells the two apart, and it is also why the buttons
     * do not need `stopPropagation` on their keydown the way they do on their click.
     *
     * Every key this handles is swallowed, including the ones that turn out to do nothing --
     * ArrowDown on the last row, ArrowRight on a childless account. The alternative is that
     * those exact keystrokes scroll the page behind an open overlay, which reads as the panel
     * having come loose.
     */
    protected onRowKeydown(row: AccountPanelRow, index: number, event: KeyboardEvent): void {
        if (event.target !== event.currentTarget) { return; }

        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                this.moveFocusTo(index + 1);
                return;

            case 'ArrowUp':
                event.preventDefault();
                this.moveFocusTo(index - 1);
                return;

            case 'Home':
                event.preventDefault();
                this.moveFocusTo(0);
                return;

            case 'End':
                event.preventDefault();
                this.moveFocusTo(this.rows().length - 1);
                return;

            case 'ArrowRight':
                event.preventDefault();
                this.onExpandKey(row, index);
                return;

            case 'ArrowLeft':
                event.preventDefault();
                this.onCollapseKey(row, index);
                return;

            case 'Enter':
            case ' ':
            case 'Spacebar':
                event.preventDefault();
                this.onSelectKey(row);
                return;

            default:
                return;
        }
    }

    /**
     * Enter and Space select the focused row, and do nothing at all on a locked one.
     *
     * Both keys are swallowed on every row, locked included. Space would otherwise scroll the
     * page out from under an open overlay, and that is a keystroke-swallowing decision rather
     * than a selection decision, which is why it happens before the lockout test rather than
     * after it.
     */
    private onSelectKey(row: AccountPanelRow): void {
        if (row.isLocked) { return; }

        this.accountSelected.emit(row.account.accountID);
    }

    /**
     * Right opens a closed parent, and steps into the first child of one that is already open.
     *
     * Right/Left was chosen over "Enter on the focused chevron" for expansion because it leaves
     * Tab free to reach the row's two buttons. Binding expansion to the chevron would have made
     * the chevron a required stop on the way to anything, so every user wanting the person-gear
     * would Tab through an expand control they did not ask for.
     *
     * A row with no children is left alone rather than toggled: the chevron is drawn on every
     * main row whether or not there is anything under it, so the glyph cannot be used as the
     * test and `hasChildren` is.
     */
    private onExpandKey(row: AccountPanelRow, index: number): void {
        if (row.kind !== 'main' || !row.hasChildren) { return; }

        if (!row.isExpanded) {
            this.expandToggled.emit(row.account.accountID);
            return;
        }

        // Already open, so the first child is the row directly below -- `rows` emits a parent's
        // children immediately after it.
        this.moveFocusTo(index + 1);
    }

    /**
     * Left closes an open parent, and steps a child back up to its parent. The two halves of
     * the same gesture: it always moves towards the root, whether by closing or by climbing.
     */
    private onCollapseKey(row: AccountPanelRow, index: number): void {
        if (row.kind === 'sub') {
            this.moveFocusToParentOf(index);
            return;
        }

        if (row.isExpanded) {
            this.expandToggled.emit(row.account.accountID);
        }
    }

    /**
     * A chevron with no sub-accounts behind it does nothing, by mouse or by key.
     *
     * This guard is what makes it inert: `aria-disabled` on a native button announces the state
     * but does not stop the button activating, and Enter and Space on a focused button arrive
     * here as the same click. The propagation is stopped first, so a press on the disabled
     * chevron does not fall through and select the row underneath it either.
     */
    protected onChevronClick(row: AccountPanelRow, event: Event): void {
        // The chevron sits inside a clickable row, so its click must not also select.
        event.stopPropagation();

        if (!row.hasChildren) { return; }

        this.expandToggled.emit(row.account.accountID);
    }

    protected onSettingsClick(row: AccountPanelRow, event: Event): void {
        event.stopPropagation();
        this.accountSettingsClicked.emit(row.account.accountID);
    }

    /**
     * PAY NOW sits inside a row whose whole body selects, so its click must not also select --
     * and on a locked row the container would refuse that selection anyway, which would look
     * like the link had done nothing.
     */
    protected onPayNowClick(row: AccountPanelRow, event: Event): void {
        event.stopPropagation();
        this.payNowClicked.emit(row.account.accountID);
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    /**
     * Which row carries the list's single tab stop: the one the user last focused, else the
     * selected account's row, else the first. The selected row is preferred over the first so
     * that opening the panel lands the keyboard on the account the user is already in, which
     * is the row they most often want to move away from by one or two steps.
     */
    private resolveActiveRowId(rows: AccountPanelRow[]): string | null {
        if (rows.length < 1) { return null; }

        // An id the current list no longer holds is ignored rather than cleared, so a row that
        // disappears and comes back -- a collapsed parent's child, say -- takes the tab stop
        // with it both ways.
        if (rows.some(row => row.account.accountID === this.focusedAccountId())) {
            return this.focusedAccountId();
        }

        return (rows.find(row => row.isSelected) ?? rows[0]).account.accountID;
    }

    /**
     * Focus the row at a position in {@link rows}, if there is one there.
     *
     * Deliberately does not wrap. The list is a visual sequence with a top and a bottom, and a
     * wrap turns "I am at the end" into a silent jump to the other end of a list the user
     * cannot see all of at once; Home and End are the way to cross it in one keystroke.
     */
    private moveFocusTo(index: number): void {
        this.rowElementAt(index)?.focus();
    }

    /**
     * Focus the parent of the sub-account row at `index`: the nearest main row above it. Found
     * by walking back through the flattened list rather than by reading `parentAccountID`,
     * because what Left has to move to is the parent ROW the user can see, and a sub-account
     * whose parent is not on screen cannot be reached by this key in the first place.
     */
    private moveFocusToParentOf(index: number): void {
        const rows = this.rows();

        for (let candidate = index - 1; candidate >= 0; candidate--) {
            if (rows[candidate].kind === 'main') {
                this.moveFocusTo(candidate);
                return;
            }
        }
    }

    /**
     * Re-focus the row for {@link focusedAccountId}, but ONLY when the element that held focus
     * has been destroyed and the browser has dropped focus on the body as a result.
     *
     * Both conditions matter, and each one on its own is a bug:
     *
     * - Without the `isConnected` test this would fire on every change-detection pass and fight
     *   the user for focus the moment they tabbed to one of the row's own buttons.
     * - Without the `document.body` test it would reach out and grab focus back from wherever
     *   the user had legitimately moved to, on any rebuild, from a panel they may have stopped
     *   caring about.
     *
     * Together they name exactly one situation: the row under the user's fingers was replaced
     * underneath them, and nobody else has claimed focus since.
     */
    private restoreFocusAfterRebuild(): void {
        const previous = this.focusedRowElement;
        if (previous == null || previous.isConnected) { return; }

        const active = document.activeElement;
        if (active != null && active !== document.body) {
            this.focusedRowElement = null;
            return;
        }

        // Resolved by account id, so the row is found wherever the rebuilt list has put it --
        // including at a different position, which a rename can cause on its own.
        const index = this.rows().findIndex(row => row.account.accountID === this.focusedAccountId());
        const replacement = this.rowElementAt(index);

        this.focusedRowElement = replacement;
        replacement?.focus();
    }

    private focusActiveRow(): void {
        const index = this.rows().findIndex(row => row.isActive);
        this.rowElementAt(index)?.focus();
    }

    private rowElementAt(index: number): HTMLElement | null {
        if (index < 0) { return null; }
        return this.rowElements()[index]?.nativeElement ?? null;
    }

    protected trackByAccountId(_index: number, row: AccountPanelRow): string {
        return row.account.accountID;
    }

    private isExpanded(account: IcAccount): boolean {
        return this.expandedAccountIds()?.includes(account.accountID) ?? false;
    }

    private buildRow(account: IcAccount, kind: AccountRowKind, isExpanded: boolean, posInSet: number, setSize: number): AccountPanelRow {
        const isLocked = account.paymentLockout === true;

        return {
            account,
            kind,
            isSelected: account.accountID === this.selectedAccountId(),
            // Set for the whole list at once by `rows`, which is the only place that
            // can see every row and so the only place that can pick exactly one of them.
            isActive: false,
            isExpanded,
            posInSet,
            setSize,
            hasChildren: (account.subAccounts?.length ?? 0) > 0,
            metaSegments: this.composeMetaSegments(account),
            isLocked,
            canPayNow: isLocked && this.canPayBilling(account)
        };
    }

    /**
     * The same check `/account/account-select` makes before offering PAY NOW, against the same
     * bitmask. It reads `account.permissions` as a raw flags value, which is why the batched
     * account read upstream has to preserve that value byte for byte: drift by a single flag
     * and this link appears for users who cannot pay, or vanishes for users who can.
     */
    private canPayBilling(account: IcAccount): boolean {
        return FlagsHelper.hasFlag(account.permissions, Permissions.Billing);
    }

    /**
     * `#{organizationNumber}` / `{online}/{total}` / `{n} {noun}`, in that order.
     *
     * A segment whose data is absent is dropped rather than rendered empty, so no row can
     * show a dangling separator. Three rules are deliberate:
     *
     * - The account number is dropped at zero as well as when it is missing. IcAccount
     *   initialises organizationNumber to 0, so an account sent without one arrives as 0, and
     *   a `#0` would read as a real account number.
     * - The sub-account segment is dropped at a count of zero. The design's sample shows it
     *   on childless rows too, but that sample is internally inconsistent (section 4 of the
     *   measured spec) and is not a business rule.
     * - Absent controller counts are dropped like any other absent segment, which is what
     *   happens wherever the stage-1 backend has not been deployed. A zero total is real data
     *   and renders `0/0`. A number nobody measured must never render: a wrong online figure
     *   is actively misleading about irrigation state, and ER-1280 is explicit about it.
     */
    private composeMetaSegments(account: IcAccount): string[] {
        const segments: string[] = [];

        if (account.organizationNumber > 0) {
            segments.push(`#${account.organizationNumber}`);
        }

        if (account.controllerCount != null) {
            segments.push(`${account.controllersOnlineCount ?? 0}/${account.controllerCount}`);
        }

        const subAccountCount = account.subAccounts?.length ?? 0;
        if (subAccountCount > 0) {
            segments.push(`${subAccountCount} ${this.subAccountNoun(subAccountCount)}`);
        }

        return segments;
    }

    /**
     * `Sub account` / `Sub accounts` — capital S, lowercase a, per the design's verbatim copy.
     * Distinct from the existing `STRINGS.SUB_ACCOUNTS` ("Sub Accounts"), which is Title Case
     * and used as a heading; these two read inline inside the meta line. Both are on the
     * stage-4 i18n key delta for sign-off.
     */
    private subAccountNoun(count: number): string {
        return this.translate.instant(count === 1 ? 'STRINGS.SUB_ACCOUNT_META_SINGULAR' : 'STRINGS.SUB_ACCOUNT_META_PLURAL');
    }
}
